import { ConfigurationStoreState, useConfigurationStore } from '@stores/configurationStore'
import { useJackpotStore } from '@stores/jackpotStore'
import { SlotContent } from '@utils/Slot'
import React, { useEffect, useRef, useState } from 'react'
import JackpotStatisticsEQ from './JackpotStatisticsEQ'
import { defaultJackpotEQConfig } from './config'
import JackpotEQStatisticsItem from './JackpotEQStatisticsItem'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import {
    EQLevelReachedMessage,
    GameUIMessageScope,
    GameUIMessageType,
    JackpotEQIndexLevelMap,
    JackpotEQLevels,
    ViewportType,
} from 'game-ui-types'

/**
 * JackpotEQSection Component
 *
 * Handles rendering of all egypt quest.
 */
const JackpotEQSection: React.FC = () => {
    const viewport = useConfigurationStore((state: ConfigurationStoreState) => state.viewport)
    const jackpotEQSectionConfigPositions = useConfigurationStore(
        (state) => state.getComponentConfig('JackpotEQSection').position
    )
    const isJackpotEQRunning = useJackpotStore((state) => state.isJackpotEQStarted)
    const freezeIndex = useJackpotStore((store) => store.freezeIndex)
    const [isSwapAnimationCompleted, setIsSwapAnimationCompleted] = useState(true)
    const [activeLevelIndex, setActiveLevelIndex] = useState(freezeIndex)
    const [isIntroInProgress, setIsIntroInProgress] = useState(false)

    const shouldSetEQTopStatisticsPosition = isJackpotEQRunning && !isIntroInProgress && isSwapAnimationCompleted

    const [showTooltipLevel, setShowTooltipLevel] = useState<JackpotEQLevels | null>(null)
    const slotTopRef = useRef<HTMLDivElement>(null)

    const handleClick = (level: JackpotEQLevels) => {
        if (level === showTooltipLevel) setShowTooltipLevel(null)
        else setShowTooltipLevel(level)
    }

    const handleMouseOver =
        viewport !== ViewportType.DESKTOP
            ? undefined
            : (level: JackpotEQLevels) => {
                  setShowTooltipLevel(level)
              }

    const handleMouseOut =
        viewport !== ViewportType.DESKTOP
            ? undefined
            : () => {
                  setShowTooltipLevel(null)
              }

    const getEQTopStatisticsTranslateX = () => (viewport === ViewportType.DESKTOP ? 480 : 356)

    useEffect(() => {
        if (!slotTopRef.current) {
            return
        }

        const translateX =
            shouldSetEQTopStatisticsPosition && viewport !== ViewportType.MOBILE_PORTRAIT
                ? getEQTopStatisticsTranslateX()
                : 0

        slotTopRef.current.style.transform = `translateX(${translateX}px)`
    }, [shouldSetEQTopStatisticsPosition, viewport])

    useGSAP(() => {
        if (isIntroInProgress || isSwapAnimationCompleted) {
            return
        }

        gsap.killTweensOf(slotTopRef.current)

        if (slotTopRef.current && viewport !== ViewportType.MOBILE_PORTRAIT) {
            gsap.fromTo(
                slotTopRef.current,
                { x: 0 },
                {
                    x: getEQTopStatisticsTranslateX(),
                    duration: 1,
                    ease: 'power2.easeInOut',
                    onComplete: () => setIsSwapAnimationCompleted(true),
                }
            )
        } else {
            setIsSwapAnimationCompleted(true)
        }
    }, [isIntroInProgress, isSwapAnimationCompleted, viewport])

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const rawData = event.data
            const { type, scope } = rawData

            if (scope !== GameUIMessageScope.JACKPOT_EQ) {
                return
            }

            switch (type) {
                case GameUIMessageType.EQ_FREESPINS_INTRO_STARTED: {
                    setIsIntroInProgress(true)
                    setIsSwapAnimationCompleted(false)
                    break
                }
                case GameUIMessageType.EQ_LEVEL_REACHED: {
                    const data = rawData as EQLevelReachedMessage

                    setActiveLevelIndex(data.levelIndex)
                    break
                }
                case GameUIMessageType.JACKPOT_EQ_ENDED: {
                    setActiveLevelIndex(-1)
                    break
                }
                case GameUIMessageType.EQ_FREESPINS_INTRO_ENDED: {
                    setIsIntroInProgress(false)
                    setIsSwapAnimationCompleted(false)
                    break
                }
            }
        }

        window.addEventListener('message', handleMessage, false)

        return () => {
            window.removeEventListener('message', handleMessage, false)
        }
    }, [])

    const platinumItem = (
        <JackpotEQStatisticsItem
            type={JackpotEQLevels.PLATINUM}
            onClick={handleClick}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
            isTooltipActive={showTooltipLevel === JackpotEQLevels.PLATINUM}
            isPanelActive={
                activeLevelIndex !== null &&
                JackpotEQIndexLevelMap[activeLevelIndex as keyof typeof JackpotEQIndexLevelMap] ===
                    JackpotEQLevels.PLATINUM
            }
        />
    )

    const goldItem = (
        <JackpotEQStatisticsItem
            type={JackpotEQLevels.GOLD}
            onClick={handleClick}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
            isTooltipActive={showTooltipLevel === JackpotEQLevels.GOLD}
            isPanelActive={
                activeLevelIndex !== null &&
                JackpotEQIndexLevelMap[activeLevelIndex as keyof typeof JackpotEQIndexLevelMap] === JackpotEQLevels.GOLD
            }
        />
    )

    const silverItem = (
        <JackpotEQStatisticsItem
            type={JackpotEQLevels.SILVER}
            onClick={handleClick}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
            isTooltipActive={showTooltipLevel === JackpotEQLevels.SILVER}
            isPanelActive={
                activeLevelIndex !== null &&
                JackpotEQIndexLevelMap[activeLevelIndex as keyof typeof JackpotEQIndexLevelMap] ===
                    JackpotEQLevels.SILVER
            }
        />
    )

    const bronzeItem = (
        <JackpotEQStatisticsItem
            type={JackpotEQLevels.BRONZE}
            onClick={handleClick}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
            isTooltipActive={showTooltipLevel === JackpotEQLevels.BRONZE}
            isPanelActive={
                activeLevelIndex !== null &&
                JackpotEQIndexLevelMap[activeLevelIndex as keyof typeof JackpotEQIndexLevelMap] ===
                    JackpotEQLevels.BRONZE
            }
        />
    )

    const topSlotContent = (
        <SlotContent slot="top">
            <div
                ref={slotTopRef}
                style={{
                    display: 'flex',
                    justifyContent: viewport !== ViewportType.MOBILE_PORTRAIT ? 'flex-start' : 'space-between',
                    width: viewport !== ViewportType.MOBILE_PORTRAIT ? 'auto' : '100%',
                }}
            >
                {platinumItem}
                {goldItem}
            </div>
        </SlotContent>
    )

    const bottomSlotContent = (
        <SlotContent slot="bottom">
            {silverItem}
            {bronzeItem}
        </SlotContent>
    )

    return (
        <>
            <JackpotStatisticsEQ
                globalConfigPositions={jackpotEQSectionConfigPositions}
                config={defaultJackpotEQConfig}
            >
                {topSlotContent}
                {bottomSlotContent}
            </JackpotStatisticsEQ>

            {isJackpotEQRunning}

            {viewport !== ViewportType.DESKTOP && showTooltipLevel && (
                <div className="overlay w-full h-full top-0 left-0 fixed" onClick={() => setShowTooltipLevel(null)} />
            )}
        </>
    )
}

export default JackpotEQSection
